import jsPDF from "jspdf";

interface PdfTranslations {
  title: string;
  subtitle: string;
  generatedOn: string;
  requirementsTitle: string;
  recommendationTitle: string;
  recommendedProduct: string;
  reasoning: string;
  performanceSection: string;
  capacitySection: string;
  vpnSection: string;
  networkSection: string;
  featuresSection: string;
  interfacesSection: string;
  industrialSection: string;
  yes: string;
  no: string;
  disclaimer: string;
  contactTitle: string;
  contactText: string;
}

interface SimpleRequirements {
  users: number;
  vpnConnections: number;
  redundancy: boolean;
  budget: "low" | "medium" | "high";
  firewallThroughput: number;
  ipsThroughput: number;
}

interface AdvancedRequirements {
  firewallThroughputUdp: number;
  firewallThroughputImix: number;
  ipsThroughputUdp: number;
  ipsThroughputImix: number;
  vpnThroughputAes: number;
  threatPreventionThroughput: number;
  users: number;
  ngfwUsers: number;
  concurrentConnections: number;
  newConnectionsPerSecond: number;
  vpnIpsecTunnels: number;
  vpnSslTunnels: number;
  maxRules: number;
  maxStaticRoutes: number;
  maxQosQueues: number;
  maxLogicalInterfaces: number;
  highAvailability: boolean;
  redundantPowerSupply: boolean;
  redundantDisk: boolean;
  tpmChipset: boolean;
  needsCopper1g: boolean;
  needsCopper2_5g: boolean;
  needsFiber1g: boolean;
  needsFiber10g: boolean;
  needsFiber25g: boolean;
  needsFiber40g: boolean;
  dinRail: boolean;
  fanLess: boolean;
  industrialProtocols: boolean;
}

interface Recommendation {
  productSlug: string;
  productName: string;
  reason: string;
}

export interface ProductSpecifications {
  firewall_users: number | null;
  ngfw_users: number | null;
  firewall_throughput_udp: number | null;
  firewall_throughput_imix: number | null;
  ips_throughput_udp: number | null;
  ips_throughput_imix: number | null;
  vpn_throughput_aes: number | null;
  vpn_throughput_aes_imix: number | null;
  threat_prevention_throughput: number | null;
  concurrent_connections: number | null;
  new_connections_per_second: number | null;
  max_rules: number | null;
  max_vpn_ipsec_tunnels: number | null;
  max_vpn_ssl_tunnels: number | null;
  max_static_routes: number | null;
  max_qos_queues: number | null;
  max_logical_interfaces: number | null;
  high_availability: boolean | null;
  redundant_power_supply: string | null;
  redundant_disk: boolean | null;
  copper_interfaces_1g: string | null;
  copper_interfaces_2_5g: string | null;
  fiber_interfaces_1g: string | null;
  fiber_interfaces_10g: string | null;
  fiber_interfaces_25g: string | null;
  fiber_interfaces_40g: string | null;
  form_factor: string | null;
  initial_storage: string | null;
  tpm_chipset: boolean | null;
  din_rail: boolean | null;
  fan_less: boolean | null;
  industrial_protocols: string | null;
}

const translations: Record<string, PdfTranslations> = {
  nl: {
    title: "Firewall Adviesrapport",
    subtitle: "Gepersonaliseerd advies op basis van uw vereisten",
    generatedOn: "Gegenereerd op",
    requirementsTitle: "Uw Vereisten",
    recommendationTitle: "Onze Aanbeveling",
    recommendedProduct: "Aanbevolen product",
    reasoning: "Onderbouwing",
    performanceSection: "Performance",
    capacitySection: "Capaciteit",
    vpnSection: "VPN",
    networkSection: "Netwerk",
    featuresSection: "Features",
    interfacesSection: "Interfaces",
    industrialSection: "Industrieel",
    yes: "Ja",
    no: "Nee",
    disclaimer: "Dit advies is gegenereerd op basis van de door u opgegeven vereisten. Voor een definitief advies raden wij aan contact op te nemen met onze specialisten.",
    contactTitle: "Neem contact op",
    contactText: "Heeft u vragen over dit advies? Neem gerust contact met ons op voor persoonlijk advies.",
  },
  en: {
    title: "Firewall Advisory Report",
    subtitle: "Personalized advice based on your requirements",
    generatedOn: "Generated on",
    requirementsTitle: "Your Requirements",
    recommendationTitle: "Our Recommendation",
    recommendedProduct: "Recommended product",
    reasoning: "Reasoning",
    performanceSection: "Performance",
    capacitySection: "Capacity",
    vpnSection: "VPN",
    networkSection: "Network",
    featuresSection: "Features",
    interfacesSection: "Interfaces",
    industrialSection: "Industrial",
    yes: "Yes",
    no: "No",
    disclaimer: "This advice is generated based on the requirements you provided. For definitive advice, we recommend contacting our specialists.",
    contactTitle: "Contact us",
    contactText: "Do you have questions about this advice? Feel free to contact us for personal advice.",
  },
  fr: {
    title: "Rapport de Conseil Firewall",
    subtitle: "Conseils personnalisés basés sur vos exigences",
    generatedOn: "Généré le",
    requirementsTitle: "Vos Exigences",
    recommendationTitle: "Notre Recommandation",
    recommendedProduct: "Produit recommandé",
    reasoning: "Raisonnement",
    performanceSection: "Performance",
    capacitySection: "Capacité",
    vpnSection: "VPN",
    networkSection: "Réseau",
    featuresSection: "Fonctionnalités",
    interfacesSection: "Interfaces",
    industrialSection: "Industriel",
    yes: "Oui",
    no: "Non",
    disclaimer: "Ce conseil est généré sur la base des exigences que vous avez fournies. Pour un conseil définitif, nous vous recommandons de contacter nos spécialistes.",
    contactTitle: "Contactez-nous",
    contactText: "Avez-vous des questions sur ce conseil? N'hésitez pas à nous contacter pour un conseil personnalisé.",
  },
};

const fieldLabels: Record<string, Record<string, string>> = {
  nl: {
    users: "Aantal gebruikers",
    vpnConnections: "VPN verbindingen",
    redundancy: "High Availability",
    budget: "Budget",
    firewallThroughput: "Firewall throughput",
    ipsThroughput: "IPS throughput",
    firewallThroughputUdp: "Firewall throughput UDP",
    firewallThroughputImix: "Firewall throughput IMIX",
    ipsThroughputUdp: "IPS throughput UDP",
    ipsThroughputImix: "IPS throughput IMIX",
    vpnThroughputAes: "VPN throughput AES",
    threatPreventionThroughput: "Threat Prevention throughput",
    ngfwUsers: "NGFW gebruikers",
    concurrentConnections: "Gelijktijdige verbindingen",
    newConnectionsPerSecond: "Nieuwe verbindingen/sec",
    vpnIpsecTunnels: "IPSec VPN tunnels",
    vpnSslTunnels: "SSL VPN tunnels",
    maxRules: "Maximaal aantal regels",
    maxStaticRoutes: "Statische routes",
    maxQosQueues: "QoS queues",
    maxLogicalInterfaces: "Logische interfaces",
    highAvailability: "High Availability",
    redundantPowerSupply: "Redundante voeding",
    redundantDisk: "Redundante disk",
    tpmChipset: "TPM chipset",
    needsCopper1g: "Copper 1G poorten",
    needsCopper2_5g: "Copper 2.5G poorten",
    needsFiber1g: "Fiber 1G poorten",
    needsFiber10g: "Fiber 10G poorten",
    needsFiber25g: "Fiber 25G poorten",
    needsFiber40g: "Fiber 40G poorten",
    dinRail: "DIN Rail montage",
    fanLess: "Fanless design",
    industrialProtocols: "Industriële protocollen",
    low: "Budget-vriendelijk",
    medium: "Gemiddeld",
    high: "Premium",
  },
  en: {
    users: "Number of users",
    vpnConnections: "VPN connections",
    redundancy: "High Availability",
    budget: "Budget",
    firewallThroughput: "Firewall throughput",
    ipsThroughput: "IPS throughput",
    firewallThroughputUdp: "Firewall throughput UDP",
    firewallThroughputImix: "Firewall throughput IMIX",
    ipsThroughputUdp: "IPS throughput UDP",
    ipsThroughputImix: "IPS throughput IMIX",
    vpnThroughputAes: "VPN throughput AES",
    threatPreventionThroughput: "Threat Prevention throughput",
    ngfwUsers: "NGFW users",
    concurrentConnections: "Concurrent connections",
    newConnectionsPerSecond: "New connections/sec",
    vpnIpsecTunnels: "IPSec VPN tunnels",
    vpnSslTunnels: "SSL VPN tunnels",
    maxRules: "Maximum rules",
    maxStaticRoutes: "Static routes",
    maxQosQueues: "QoS queues",
    maxLogicalInterfaces: "Logical interfaces",
    highAvailability: "High Availability",
    redundantPowerSupply: "Redundant power supply",
    redundantDisk: "Redundant disk",
    tpmChipset: "TPM chipset",
    needsCopper1g: "Copper 1G ports",
    needsCopper2_5g: "Copper 2.5G ports",
    needsFiber1g: "Fiber 1G ports",
    needsFiber10g: "Fiber 10G ports",
    needsFiber25g: "Fiber 25G ports",
    needsFiber40g: "Fiber 40G ports",
    dinRail: "DIN Rail mounting",
    fanLess: "Fanless design",
    industrialProtocols: "Industrial protocols",
    low: "Budget-friendly",
    medium: "Medium",
    high: "Premium",
  },
  fr: {
    users: "Nombre d'utilisateurs",
    vpnConnections: "Connexions VPN",
    redundancy: "Haute disponibilité",
    budget: "Budget",
    firewallThroughput: "Débit firewall",
    ipsThroughput: "Débit IPS",
    firewallThroughputUdp: "Débit firewall UDP",
    firewallThroughputImix: "Débit firewall IMIX",
    ipsThroughputUdp: "Débit IPS UDP",
    ipsThroughputImix: "Débit IPS IMIX",
    vpnThroughputAes: "Débit VPN AES",
    threatPreventionThroughput: "Débit Threat Prevention",
    ngfwUsers: "Utilisateurs NGFW",
    concurrentConnections: "Connexions simultanées",
    newConnectionsPerSecond: "Nouvelles connexions/sec",
    vpnIpsecTunnels: "Tunnels VPN IPSec",
    vpnSslTunnels: "Tunnels VPN SSL",
    maxRules: "Règles maximum",
    maxStaticRoutes: "Routes statiques",
    maxQosQueues: "Files QoS",
    maxLogicalInterfaces: "Interfaces logiques",
    highAvailability: "Haute disponibilité",
    redundantPowerSupply: "Alimentation redondante",
    redundantDisk: "Disque redondant",
    tpmChipset: "Puce TPM",
    needsCopper1g: "Ports Copper 1G",
    needsCopper2_5g: "Ports Copper 2.5G",
    needsFiber1g: "Ports Fiber 1G",
    needsFiber10g: "Ports Fiber 10G",
    needsFiber25g: "Ports Fiber 25G",
    needsFiber40g: "Ports Fiber 40G",
    dinRail: "Montage DIN Rail",
    fanLess: "Design sans ventilateur",
    industrialProtocols: "Protocoles industriels",
    low: "Économique",
    medium: "Moyen",
    high: "Premium",
  },
};

const specLabels: Record<string, Record<string, string>> = {
  nl: {
    productSpecsTitle: "Technische Specificaties Aanbevolen Product",
    firewallUsers: "Max. Firewall Gebruikers",
    ngfwUsers: "Max. NGFW Gebruikers",
    firewallThroughputUdp: "Firewall Throughput (UDP)",
    firewallThroughputImix: "Firewall Throughput (IMIX)",
    ipsThroughputUdp: "IPS Throughput (UDP)",
    ipsThroughputImix: "IPS Throughput (IMIX)",
    vpnThroughputAes: "VPN Throughput (AES)",
    vpnThroughputAesImix: "VPN Throughput (AES IMIX)",
    threatPreventionThroughput: "Threat Prevention Throughput",
    concurrentConnections: "Gelijktijdige Verbindingen",
    newConnectionsPerSecond: "Nieuwe Verbindingen/sec",
    maxRules: "Max. Regels",
    maxVpnIpsecTunnels: "Max. IPSec VPN Tunnels",
    maxVpnSslTunnels: "Max. SSL VPN Tunnels",
    maxStaticRoutes: "Max. Statische Routes",
    maxQosQueues: "Max. QoS Queues",
    maxLogicalInterfaces: "Max. Logische Interfaces",
    highAvailability: "High Availability",
    redundantPowerSupply: "Redundante Voeding",
    redundantDisk: "Redundante Disk",
    copperInterfaces1g: "Copper 1G Interfaces",
    copperInterfaces2_5g: "Copper 2.5G Interfaces",
    fiberInterfaces1g: "Fiber 1G Interfaces",
    fiberInterfaces10g: "Fiber 10G Interfaces",
    fiberInterfaces25g: "Fiber 25G Interfaces",
    fiberInterfaces40g: "Fiber 40G Interfaces",
    formFactor: "Form Factor",
    initialStorage: "Opslag",
    tpmChipset: "TPM Chipset",
    dinRail: "DIN Rail Montage",
    fanLess: "Fanless Design",
    industrialProtocols: "Industriële Protocollen",
    yes: "Ja",
    no: "Nee",
    performanceSection: "Performance",
    capacitySection: "Capaciteit",
    vpnSection: "VPN",
    networkSection: "Netwerk",
    interfacesSection: "Interfaces",
    hardwareSection: "Hardware",
    industrialSection: "Industrieel",
  },
  en: {
    productSpecsTitle: "Technical Specifications Recommended Product",
    firewallUsers: "Max. Firewall Users",
    ngfwUsers: "Max. NGFW Users",
    firewallThroughputUdp: "Firewall Throughput (UDP)",
    firewallThroughputImix: "Firewall Throughput (IMIX)",
    ipsThroughputUdp: "IPS Throughput (UDP)",
    ipsThroughputImix: "IPS Throughput (IMIX)",
    vpnThroughputAes: "VPN Throughput (AES)",
    vpnThroughputAesImix: "VPN Throughput (AES IMIX)",
    threatPreventionThroughput: "Threat Prevention Throughput",
    concurrentConnections: "Concurrent Connections",
    newConnectionsPerSecond: "New Connections/sec",
    maxRules: "Max. Rules",
    maxVpnIpsecTunnels: "Max. IPSec VPN Tunnels",
    maxVpnSslTunnels: "Max. SSL VPN Tunnels",
    maxStaticRoutes: "Max. Static Routes",
    maxQosQueues: "Max. QoS Queues",
    maxLogicalInterfaces: "Max. Logical Interfaces",
    highAvailability: "High Availability",
    redundantPowerSupply: "Redundant Power Supply",
    redundantDisk: "Redundant Disk",
    copperInterfaces1g: "Copper 1G Interfaces",
    copperInterfaces2_5g: "Copper 2.5G Interfaces",
    fiberInterfaces1g: "Fiber 1G Interfaces",
    fiberInterfaces10g: "Fiber 10G Interfaces",
    fiberInterfaces25g: "Fiber 25G Interfaces",
    fiberInterfaces40g: "Fiber 40G Interfaces",
    formFactor: "Form Factor",
    initialStorage: "Storage",
    tpmChipset: "TPM Chipset",
    dinRail: "DIN Rail Mounting",
    fanLess: "Fanless Design",
    industrialProtocols: "Industrial Protocols",
    yes: "Yes",
    no: "No",
    performanceSection: "Performance",
    capacitySection: "Capacity",
    vpnSection: "VPN",
    networkSection: "Network",
    interfacesSection: "Interfaces",
    hardwareSection: "Hardware",
    industrialSection: "Industrial",
  },
  fr: {
    productSpecsTitle: "Spécifications Techniques du Produit Recommandé",
    firewallUsers: "Max. Utilisateurs Firewall",
    ngfwUsers: "Max. Utilisateurs NGFW",
    firewallThroughputUdp: "Débit Firewall (UDP)",
    firewallThroughputImix: "Débit Firewall (IMIX)",
    ipsThroughputUdp: "Débit IPS (UDP)",
    ipsThroughputImix: "Débit IPS (IMIX)",
    vpnThroughputAes: "Débit VPN (AES)",
    vpnThroughputAesImix: "Débit VPN (AES IMIX)",
    threatPreventionThroughput: "Débit Threat Prevention",
    concurrentConnections: "Connexions Simultanées",
    newConnectionsPerSecond: "Nouvelles Connexions/sec",
    maxRules: "Max. Règles",
    maxVpnIpsecTunnels: "Max. Tunnels VPN IPSec",
    maxVpnSslTunnels: "Max. Tunnels VPN SSL",
    maxStaticRoutes: "Max. Routes Statiques",
    maxQosQueues: "Max. Files QoS",
    maxLogicalInterfaces: "Max. Interfaces Logiques",
    highAvailability: "Haute Disponibilité",
    redundantPowerSupply: "Alimentation Redondante",
    redundantDisk: "Disque Redondant",
    copperInterfaces1g: "Interfaces Copper 1G",
    copperInterfaces2_5g: "Interfaces Copper 2.5G",
    fiberInterfaces1g: "Interfaces Fiber 1G",
    fiberInterfaces10g: "Interfaces Fiber 10G",
    fiberInterfaces25g: "Interfaces Fiber 25G",
    fiberInterfaces40g: "Interfaces Fiber 40G",
    formFactor: "Format",
    initialStorage: "Stockage",
    tpmChipset: "Puce TPM",
    dinRail: "Montage DIN Rail",
    fanLess: "Design Sans Ventilateur",
    industrialProtocols: "Protocoles Industriels",
    yes: "Oui",
    no: "Non",
    performanceSection: "Performance",
    capacitySection: "Capacité",
    vpnSection: "VPN",
    networkSection: "Réseau",
    interfacesSection: "Interfaces",
    hardwareSection: "Matériel",
    industrialSection: "Industriel",
  },
};

export function generateFirewallPdf(
  mode: "simple" | "advanced",
  requirements: SimpleRequirements | AdvancedRequirements,
  recommendation: Recommendation,
  language: string,
  productSpecs?: ProductSpecifications | null
): void {
  const t = translations[language] || translations.nl;
  const labels = fieldLabels[language] || fieldLabels.nl;
  
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let yPos = margin;

  // Colors
  const primaryColor: [number, number, number] = [30, 64, 175]; // Blue
  const textColor: [number, number, number] = [31, 41, 55];
  const mutedColor: [number, number, number] = [107, 114, 128];

  // Helper functions
  const addText = (text: string, x: number, y: number, options?: { 
    fontSize?: number; 
    fontStyle?: string; 
    color?: [number, number, number];
    maxWidth?: number;
  }) => {
    doc.setFontSize(options?.fontSize || 10);
    doc.setFont("helvetica", options?.fontStyle || "normal");
    doc.setTextColor(...(options?.color || textColor));
    
    if (options?.maxWidth) {
      const lines = doc.splitTextToSize(text, options.maxWidth);
      doc.text(lines, x, y);
      return lines.length * (options?.fontSize || 10) * 0.4;
    }
    doc.text(text, x, y);
    return (options?.fontSize || 10) * 0.4;
  };

  const addLine = (y: number) => {
    doc.setDrawColor(229, 231, 235);
    doc.line(margin, y, pageWidth - margin, y);
  };

  const checkPageBreak = (neededSpace: number) => {
    if (yPos + neededSpace > doc.internal.pageSize.getHeight() - margin) {
      doc.addPage();
      yPos = margin;
    }
  };

  // Header with branding
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, 45, "F");

  addText("CYSCOM", margin, 20, { fontSize: 24, fontStyle: "bold", color: [255, 255, 255] });
  addText(t.title, margin, 32, { fontSize: 14, color: [255, 255, 255] });
  addText(t.subtitle, margin, 40, { fontSize: 10, color: [200, 210, 255] });

  yPos = 60;

  // Date
  const today = new Date().toLocaleDateString(language === "nl" ? "nl-NL" : language === "fr" ? "fr-FR" : "en-GB", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  addText(`${t.generatedOn}: ${today}`, margin, yPos, { fontSize: 9, color: mutedColor });
  yPos += 15;

  // Recommendation section (highlighted)
  doc.setFillColor(240, 253, 244);
  doc.roundedRect(margin, yPos, contentWidth, 50, 3, 3, "F");
  doc.setDrawColor(34, 197, 94);
  doc.roundedRect(margin, yPos, contentWidth, 50, 3, 3, "S");

  yPos += 12;
  addText(t.recommendationTitle, margin + 10, yPos, { fontSize: 14, fontStyle: "bold", color: [22, 101, 52] });
  yPos += 10;
  addText(`${t.recommendedProduct}: ${recommendation.productName}`, margin + 10, yPos, { fontSize: 12, fontStyle: "bold", color: textColor });
  yPos += 10;
  
  const reasonLines = doc.splitTextToSize(recommendation.reason, contentWidth - 20);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...textColor);
  doc.text(reasonLines, margin + 10, yPos);
  yPos += reasonLines.length * 5 + 20;

  // Requirements section
  addLine(yPos);
  yPos += 10;
  addText(t.requirementsTitle, margin, yPos, { fontSize: 14, fontStyle: "bold", color: primaryColor });
  yPos += 12;

  if (mode === "simple") {
    const simpleReqs = requirements as SimpleRequirements;
    const simpleItems = [
      { label: labels.users, value: simpleReqs.users.toString() },
      { label: labels.vpnConnections, value: simpleReqs.vpnConnections.toString() },
      { label: labels.firewallThroughput, value: `${simpleReqs.firewallThroughput} Gbps` },
      { label: labels.ipsThroughput, value: `${simpleReqs.ipsThroughput} Gbps` },
      { label: labels.redundancy, value: simpleReqs.redundancy ? t.yes : t.no },
      { label: labels.budget, value: labels[simpleReqs.budget] },
    ];

    simpleItems.forEach((item) => {
      checkPageBreak(8);
      addText(`${item.label}:`, margin, yPos, { fontSize: 10, fontStyle: "bold" });
      addText(item.value, margin + 80, yPos, { fontSize: 10 });
      yPos += 7;
    });
  } else {
    const advReqs = requirements as AdvancedRequirements;

    // Group advanced requirements by section
    const sections = [
      {
        title: t.performanceSection,
        items: [
          { key: "firewallThroughputUdp", value: advReqs.firewallThroughputUdp, unit: " Gbps" },
          { key: "firewallThroughputImix", value: advReqs.firewallThroughputImix, unit: " Gbps" },
          { key: "ipsThroughputUdp", value: advReqs.ipsThroughputUdp, unit: " Gbps" },
          { key: "ipsThroughputImix", value: advReqs.ipsThroughputImix, unit: " Gbps" },
          { key: "vpnThroughputAes", value: advReqs.vpnThroughputAes, unit: " Gbps" },
          { key: "threatPreventionThroughput", value: advReqs.threatPreventionThroughput, unit: " Gbps" },
        ].filter(i => i.value > 0),
      },
      {
        title: t.capacitySection,
        items: [
          { key: "users", value: advReqs.users, unit: "" },
          { key: "ngfwUsers", value: advReqs.ngfwUsers, unit: "" },
          { key: "concurrentConnections", value: advReqs.concurrentConnections, unit: "" },
          { key: "newConnectionsPerSecond", value: advReqs.newConnectionsPerSecond, unit: "" },
        ].filter(i => i.value > 0),
      },
      {
        title: t.vpnSection,
        items: [
          { key: "vpnIpsecTunnels", value: advReqs.vpnIpsecTunnels, unit: "" },
          { key: "vpnSslTunnels", value: advReqs.vpnSslTunnels, unit: "" },
        ].filter(i => i.value > 0),
      },
      {
        title: t.networkSection,
        items: [
          { key: "maxRules", value: advReqs.maxRules, unit: "" },
          { key: "maxStaticRoutes", value: advReqs.maxStaticRoutes, unit: "" },
          { key: "maxQosQueues", value: advReqs.maxQosQueues, unit: "" },
          { key: "maxLogicalInterfaces", value: advReqs.maxLogicalInterfaces, unit: "" },
        ].filter(i => i.value > 0),
      },
      {
        title: t.featuresSection,
        items: [
          { key: "highAvailability", value: advReqs.highAvailability },
          { key: "redundantPowerSupply", value: advReqs.redundantPowerSupply },
          { key: "redundantDisk", value: advReqs.redundantDisk },
          { key: "tpmChipset", value: advReqs.tpmChipset },
        ].filter(i => i.value === true),
      },
      {
        title: t.interfacesSection,
        items: [
          { key: "needsCopper1g", value: advReqs.needsCopper1g },
          { key: "needsCopper2_5g", value: advReqs.needsCopper2_5g },
          { key: "needsFiber1g", value: advReqs.needsFiber1g },
          { key: "needsFiber10g", value: advReqs.needsFiber10g },
          { key: "needsFiber25g", value: advReqs.needsFiber25g },
          { key: "needsFiber40g", value: advReqs.needsFiber40g },
        ].filter(i => i.value === true),
      },
      {
        title: t.industrialSection,
        items: [
          { key: "dinRail", value: advReqs.dinRail },
          { key: "fanLess", value: advReqs.fanLess },
          { key: "industrialProtocols", value: advReqs.industrialProtocols },
        ].filter(i => i.value === true),
      },
    ].filter(s => s.items.length > 0);

    sections.forEach((section) => {
      checkPageBreak(25);
      
      // Section header
      doc.setFillColor(249, 250, 251);
      doc.roundedRect(margin, yPos - 3, contentWidth, 8, 1, 1, "F");
      addText(section.title, margin + 5, yPos + 2, { fontSize: 11, fontStyle: "bold", color: primaryColor });
      yPos += 12;

      section.items.forEach((item) => {
        checkPageBreak(7);
        const label = labels[item.key] || item.key;
        if (typeof item.value === "boolean") {
          addText(`• ${label}`, margin + 5, yPos, { fontSize: 10 });
        } else {
          addText(`• ${label}:`, margin + 5, yPos, { fontSize: 10 });
          addText(`${item.value}${(item as any).unit || ""}`, margin + 90, yPos, { fontSize: 10, fontStyle: "bold" });
        }
        yPos += 6;
      });
      yPos += 5;
    });
  }

  // Product Specifications section (if available)
  if (productSpecs) {
    const sl = specLabels[language] || specLabels.nl;
    
    yPos += 10;
    addLine(yPos);
    yPos += 10;
    addText(sl.productSpecsTitle, margin, yPos, { fontSize: 14, fontStyle: "bold", color: primaryColor });
    yPos += 12;

    // Helper to format throughput values
    const formatThroughput = (val: number | null): string => {
      if (val === null || val === undefined) return "-";
      if (val >= 1000) return `${(val / 1000).toFixed(1)} Gbps`;
      return `${val} Mbps`;
    };

    const formatNumber = (val: number | null): string => {
      if (val === null || val === undefined) return "-";
      if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
      if (val >= 1000) return `${(val / 1000).toFixed(0)}K`;
      return val.toString();
    };

    const specSections = [
      {
        title: sl.performanceSection,
        items: [
          { label: sl.firewallThroughputUdp, value: formatThroughput(productSpecs.firewall_throughput_udp) },
          { label: sl.firewallThroughputImix, value: formatThroughput(productSpecs.firewall_throughput_imix) },
          { label: sl.ipsThroughputUdp, value: formatThroughput(productSpecs.ips_throughput_udp) },
          { label: sl.ipsThroughputImix, value: formatThroughput(productSpecs.ips_throughput_imix) },
          { label: sl.vpnThroughputAes, value: formatThroughput(productSpecs.vpn_throughput_aes) },
          { label: sl.threatPreventionThroughput, value: formatThroughput(productSpecs.threat_prevention_throughput) },
        ].filter(i => i.value !== "-"),
      },
      {
        title: sl.capacitySection,
        items: [
          { label: sl.firewallUsers, value: productSpecs.firewall_users?.toString() || "-" },
          { label: sl.ngfwUsers, value: productSpecs.ngfw_users?.toString() || "-" },
          { label: sl.concurrentConnections, value: formatNumber(productSpecs.concurrent_connections) },
          { label: sl.newConnectionsPerSecond, value: formatNumber(productSpecs.new_connections_per_second) },
        ].filter(i => i.value !== "-"),
      },
      {
        title: sl.vpnSection,
        items: [
          { label: sl.maxVpnIpsecTunnels, value: formatNumber(productSpecs.max_vpn_ipsec_tunnels) },
          { label: sl.maxVpnSslTunnels, value: formatNumber(productSpecs.max_vpn_ssl_tunnels) },
        ].filter(i => i.value !== "-"),
      },
      {
        title: sl.networkSection,
        items: [
          { label: sl.maxRules, value: formatNumber(productSpecs.max_rules) },
          { label: sl.maxStaticRoutes, value: formatNumber(productSpecs.max_static_routes) },
          { label: sl.maxQosQueues, value: formatNumber(productSpecs.max_qos_queues) },
          { label: sl.maxLogicalInterfaces, value: formatNumber(productSpecs.max_logical_interfaces) },
        ].filter(i => i.value !== "-"),
      },
      {
        title: sl.interfacesSection,
        items: [
          { label: sl.copperInterfaces1g, value: productSpecs.copper_interfaces_1g || "-" },
          { label: sl.copperInterfaces2_5g, value: productSpecs.copper_interfaces_2_5g || "-" },
          { label: sl.fiberInterfaces1g, value: productSpecs.fiber_interfaces_1g || "-" },
          { label: sl.fiberInterfaces10g, value: productSpecs.fiber_interfaces_10g || "-" },
          { label: sl.fiberInterfaces25g, value: productSpecs.fiber_interfaces_25g || "-" },
          { label: sl.fiberInterfaces40g, value: productSpecs.fiber_interfaces_40g || "-" },
        ].filter(i => i.value !== "-"),
      },
      {
        title: sl.hardwareSection,
        items: [
          { label: sl.formFactor, value: productSpecs.form_factor || "-" },
          { label: sl.initialStorage, value: productSpecs.initial_storage || "-" },
          { label: sl.highAvailability, value: productSpecs.high_availability ? sl.yes : sl.no },
          { label: sl.redundantPowerSupply, value: productSpecs.redundant_power_supply || "-" },
          { label: sl.redundantDisk, value: productSpecs.redundant_disk ? sl.yes : sl.no },
          { label: sl.tpmChipset, value: productSpecs.tpm_chipset ? sl.yes : sl.no },
        ].filter(i => i.value !== "-" && i.value !== sl.no),
      },
      {
        title: sl.industrialSection,
        items: [
          { label: sl.dinRail, value: productSpecs.din_rail ? sl.yes : sl.no },
          { label: sl.fanLess, value: productSpecs.fan_less ? sl.yes : sl.no },
          { label: sl.industrialProtocols, value: productSpecs.industrial_protocols || "-" },
        ].filter(i => i.value !== "-" && i.value !== sl.no),
      },
    ].filter(s => s.items.length > 0);

    specSections.forEach((section) => {
      checkPageBreak(25);
      
      // Section header with blue background
      doc.setFillColor(239, 246, 255);
      doc.roundedRect(margin, yPos - 3, contentWidth, 8, 1, 1, "F");
      addText(section.title, margin + 5, yPos + 2, { fontSize: 11, fontStyle: "bold", color: primaryColor });
      yPos += 12;

      section.items.forEach((item) => {
        checkPageBreak(7);
        addText(`• ${item.label}:`, margin + 5, yPos, { fontSize: 10 });
        addText(item.value, margin + 90, yPos, { fontSize: 10, fontStyle: "bold" });
        yPos += 6;
      });
      yPos += 5;
    });
  }

  // Disclaimer
  checkPageBreak(40);
  yPos += 10;
  addLine(yPos);
  yPos += 15;

  doc.setFillColor(254, 249, 195);
  doc.roundedRect(margin, yPos - 5, contentWidth, 25, 2, 2, "F");
  const disclaimerLines = doc.splitTextToSize(t.disclaimer, contentWidth - 10);
  doc.setFontSize(9);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(...mutedColor);
  doc.text(disclaimerLines, margin + 5, yPos + 3);
  yPos += 30;

  // Contact section
  checkPageBreak(30);
  addText(t.contactTitle, margin, yPos, { fontSize: 12, fontStyle: "bold", color: primaryColor });
  yPos += 8;
  addText(t.contactText, margin, yPos, { fontSize: 10, color: mutedColor, maxWidth: contentWidth });
  yPos += 15;
  addText("www.cyscom.nl | info@cyscom.nl", margin, yPos, { fontSize: 10, color: primaryColor });

  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...mutedColor);
    doc.text(
      `© ${new Date().getFullYear()} Cyscom - ${t.title}`,
      margin,
      doc.internal.pageSize.getHeight() - 10
    );
    doc.text(
      `${i} / ${pageCount}`,
      pageWidth - margin - 10,
      doc.internal.pageSize.getHeight() - 10
    );
  }

  // Save the PDF
  const filename = `firewall-advies-${recommendation.productSlug}-${new Date().toISOString().split("T")[0]}.pdf`;
  doc.save(filename);
}
